import { faPlus, faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { FlexRow, Header3Text } from 'ui-toolkit';
import { IFeeSection } from './organizeFees';
import { FeeSectionTable } from './table/FeeSectionTable';
import { useSelectCurrentOrder } from '../../../selectors/order/selectCurrentOrder';
import { StyledFeeSection, StyledFeeSectionHeader } from './FeeSections.Styled';
import { assertDefined } from 'utilities/Assertions';
import Cookie from 'js-cookie';
import { SectionHeaderTotals } from './SectionHeaderTotals';
import { Validation } from './validation/Validation';
import { EditContainer } from './edit/EditContainer';
import { FeeTabs, clientUrls } from 'ui-toolkit/rest/ClientUrlFactory';
import { useParams } from 'react-router-dom';
import { LinkTW } from 'tailwindComponents/link/LinkTW';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';

export interface IFeeSectionProps {
  feeSection: IFeeSection;
}

const purchaseNameChange = (name: string, isPurchase: boolean) => {
  if (isPurchase) {
    return name.replace('Borrower', 'Buyer');
  }

  return name;
};

export const FeeSection: React.FC<IFeeSectionProps> = ({ feeSection }) => {
  const params = useParams<{ section: FeeTabs; feeId: string }>();
  const { section, feeId } = params;
  const showEdit = feeId === feeSection.sectionDefinition.statementSectionDefinitionId;
  const hasEditPermission = useHasPermissions([ePermissions.CdItemsV3Edit]);
  const hasOptions = hasEditPermission && !!feeSection.options.length;
  const currentOrder = useSelectCurrentOrder();
  const sectionDescription = purchaseNameChange(
    feeSection.sectionDefinition.sectionDescription,
    currentOrder.data.transaction.transaction_category === eTransactionCategory.Purchase
  );
  const orderId = currentOrder.data.transaction.id;
  assertDefined(orderId);

  const getCookieName = (): string => `order${orderId}-${sectionDescription}`;

  const getAccordionInitialState = () => {
    let storedValue = Cookie.get(getCookieName());
    return storedValue === 'false' ? false : true;
  };

  const editingThisSection =
    showEdit || !!feeSection.sectionData?.statementSettlementItems.find((t) => t.settlementStatementItemId === feeId);

  const [showTable, setShowTable] = useState<boolean>(getAccordionInitialState());

  const persistAccordionState = () => {
    const date = new Date();
    date.setTime(date.getTime() + 12 * 60 * 60 * 1000);
    Cookie.set(getCookieName(), `${!showTable}`, {
      expires: date,
    });
  };

  const handleAccordion = () => {
    persistAccordionState();
    setShowTable(!showTable);
  };

  return (
    <StyledFeeSection>
      <StyledFeeSectionHeader>
        <Header3Text>
          {feeSection.sectionDefinition.sectionLetter && `${feeSection.sectionDefinition.sectionLetter}.`}
          {sectionDescription}
          {!showTable && (
            <span className="ml-8 rounded-full bg-green-100 px-2 py-1 align-middle text-xs font-medium text-green-700">
              {feeSection.settlementItems.length} Item{feeSection.settlementItems.length === 1 ? '' : 's'}
            </span>
          )}
        </Header3Text>
        {showTable && hasOptions && !showEdit && (
          <LinkTW
            isButton
            to={clientUrls.urlFor.feesTabEdit(
              orderId,
              section,
              feeSection.sectionDefinition.statementSectionDefinitionId
            )}
          >
            <FlexRow gap={'spacing2'}>
              <FontAwesomeIcon icon={faPlus} />
              Add
            </FlexRow>
          </LinkTW>
        )}
        {!showTable && (
          <SectionHeaderTotals
            feeSectionTotals={feeSection.sectionData?.sectionPropertyTotals}
            transactionCategory={currentOrder.data.transaction.transaction_category}
          />
        )}
        <FontAwesomeIcon
          icon={showTable ? faChevronUp : faChevronDown}
          size={'2x'}
          cursor={'pointer'}
          onClick={handleAccordion}
          data-test-id={'fee-expand-collapse'}
        />
      </StyledFeeSectionHeader>
      {feeSection.sectionData && (
        <Validation
          sections={[feeSection.sectionData]}
          sectionDefinitions={[feeSection.sectionDefinition]}
          className={'grid p-4'}
        />
      )}
      {showEdit && (
        <EditContainer
          layout={'Two Column'}
          section={feeSection.sectionData}
          sectionDefinition={feeSection.sectionDefinition}
          orderId={orderId}
        />
      )}
      {(showTable || editingThisSection) && (
        <FeeSectionTable
          feeSection={feeSection}
          isRefinance={currentOrder.data.transaction.transaction_category === eTransactionCategory.Refinance}
          layout={'Two Column'}
        />
      )}
    </StyledFeeSection>
  );
};
